import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AlertCircleIcon from "../../../../../components/icons/AlertCircleIcon";
import CheckCircleIcon from "../../../../../components/icons/CheckCircleIcon";
import ChevronDownIcon from "../../../../../components/icons/ChevronDownIcon";
import TrophyIcon from "../../../../../components/icons/TrophyIcon";
import Loader from "../../../../../components/loader/Loader";
import Portal from "../../../../../components/portal/Portal";
import { fetchPendingPrizesAPI } from "../../../../../features/prizeDelivery/prizeDeliveryAPI";
import { markPrizeAsDelivered } from "../../../../../features/prizeDelivery/prizeDeliverySlice";
import { formatDayDivider } from "../../../../../utils/chatTime";
import styles from "./pendingPrizesBadge.module.css";

const PANEL_WIDTH = 344;
const FRESH_MS = 60 * 1000;
// "Mark delivered" asks once more; the question lapses after this.
const CONFIRM_MS = 4000;
const PRIZE_PAGE = "/dashboard/customer-support/show-prize-Delivery";

// Each player's pending prizes as last loaded: a chat reopened within a
// minute shows them at once; an older copy shows while it refreshes.
const cache = new Map();

// Arabic count agreement for "prize": 1, 2, 3–10, 11+.
const countLabel = (count) => {
  if (count === 1) return "جائزة واحدة";
  if (count === 2) return "جائزتان";
  if (count <= 10) return `${count} جوائز`;
  return `${count} جائزة`;
};

// "loading" the first time, then "succeeded" or "failed" (retry() asks
// again). A refresh that fails keeps the copy on screen.
const usePendingPrizes = (playerId) => {
  const [state, setState] = useState(() => {
    const hit = cache.get(playerId);
    return hit ? { status: "succeeded", prizes: hit.prizes } : { status: "loading", prizes: [] };
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const hit = cache.get(playerId);
    if (attempt === 0 && hit && Date.now() - hit.at < FRESH_MS) return undefined;
    let cancelled = false;
    if (!hit) setState({ status: "loading", prizes: [] });
    fetchPendingPrizesAPI(playerId)
      .then((prizes) => {
        cache.set(playerId, { prizes, at: Date.now() });
        if (!cancelled) setState({ status: "succeeded", prizes });
      })
      .catch(() => {
        if (cancelled) return;
        setState((current) =>
          current.status === "succeeded" ? current : { status: "failed", prizes: [] },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [playerId, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  // A prize marked delivered from the panel leaves the list and the cache.
  const removePrize = useCallback(
    (tournamentId) => {
      const keep = (prize) => String(prize.tournamentId) !== tournamentId;
      setState((current) => ({ ...current, prizes: current.prizes.filter(keep) }));
      const hit = cache.get(playerId);
      if (hit) cache.set(playerId, { ...hit, prizes: hit.prizes.filter(keep) });
    },
    [playerId],
  );

  return { ...state, retry, removePrize };
};

// The prizes a player won and hasn't received, next to their name in the
// chat (mounted per player): a pill while checking, a retry if that failed,
// nothing if there are none, else the count, opening a panel with each
// tournament and its date. With mark-prizes-delivered, each one can be
// marked delivered right there (the prize page's own action, so both stay
// in step and show the same messages).
const PendingPrizesBadge = ({ playerId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const canOpenPrizePage = useSelector((state) =>
    state.user.rolePermissions.includes("view-prize-delivery"),
  );
  const canMarkDelivered = useSelector((state) =>
    state.user.rolePermissions.includes("mark-prizes-delivered"),
  );
  const { status, prizes, retry, removePrize } = usePendingPrizes(playerId);
  const [panel, setPanel] = useState(null); // { top, left } while open
  // tournamentId → "confirm" (asked once) | "saving"
  const [marking, setMarking] = useState({});
  const confirmTimers = useRef(new Map());
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const timers = confirmTimers.current;
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, []);

  const setRowState = (tournamentId, value) =>
    setMarking((current) => {
      const next = { ...current };
      if (value) next[tournamentId] = value;
      else delete next[tournamentId];
      return next;
    });

  const markDelivered = async (prize) => {
    const id = String(prize.tournamentId);
    const rowState = marking[id];
    if (rowState === "saving") return;
    clearTimeout(confirmTimers.current.get(id));

    if (rowState !== "confirm") {
      setRowState(id, "confirm");
      confirmTimers.current.set(
        id,
        setTimeout(
          () =>
            setMarking((current) => {
              if (current[id] !== "confirm") return current;
              const next = { ...current };
              delete next[id];
              return next;
            }),
          CONFIRM_MS,
        ),
      );
      return;
    }

    setRowState(id, "saving");
    try {
      await dispatch(
        markPrizeAsDelivered({ userId: playerId, tableId: String(prize.tableId), tournamentId: id }),
      ).unwrap();
      setRowState(id, null);
      removePrize(id);
      panelRef.current?.focus();
    } catch {
      // The action's toast says it failed; the prize stays to try again.
      setRowState(id, null);
    }
  };

  const close = useCallback((restoreFocus) => {
    setPanel(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const toggle = () => {
    if (panel) {
      close(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Below the pill, lined up with its right edge (RTL), kept on screen.
    const left = Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8);
    setPanel({ top: rect.bottom + 8, left: Math.max(8, left) });
  };

  useEffect(() => {
    if (!panel) return undefined;
    panelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") close(true);
    };
    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      close(false);
    };
    // Only a scroll that moves the pill closes it; the messages scroll on
    // their own and must not.
    const onScroll = (event) => {
      const scrolled = event.target === document ? document.documentElement : event.target;
      if (scrolled?.contains?.(triggerRef.current)) close(false);
    };
    const onResize = () => close(false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [panel, close]);

  if (status === "loading") {
    return (
      <span className={`${styles.pill} ${styles.pillLoading}`} role="status">
        <Loader size={12} />
        <span>جارٍ فحص الجوائز</span>
      </span>
    );
  }

  if (status === "failed") {
    return (
      <button
        type="button"
        className={`${styles.pill} ${styles.pillFailed}`}
        onClick={retry}
        title="إعادة المحاولة"
      >
        <AlertCircleIcon className={styles.icon} />
        <span>تعذّر فحص الجوائز · إعادة المحاولة</span>
      </button>
    );
  }

  if (prizes.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.pill} ${styles.pillPrizes} ${panel ? styles.pillOpen : ""}`}
        aria-haspopup="dialog"
        aria-expanded={Boolean(panel)}
        onClick={toggle}
      >
        <TrophyIcon className={styles.icon} />
        <span>{countLabel(prizes.length)} بانتظار التسليم</span>
        <ChevronDownIcon className={styles.chevron} />
      </button>

      {panel && (
        <Portal>
          <div
            ref={panelRef}
            className={styles.panel}
            style={{ top: panel.top, left: panel.left, width: PANEL_WIDTH }}
            role="dialog"
            aria-label="جوائز بانتظار التسليم"
            tabIndex={-1}
            dir="rtl"
          >
            <div className={styles.panelHeader}>
              <span className={styles.panelIcon}>
                <TrophyIcon />
              </span>
              <span className={styles.panelTitle}>جوائز بانتظار التسليم</span>
              <span className={styles.panelCount}>{prizes.length}</span>
            </div>

            <ul className={styles.list}>
              {prizes.map((prize) => {
                const meta = [
                  prize.serialNumber != null ? `#${prize.serialNumber}` : null,
                  prize.startDate ? formatDayDivider(new Date(prize.startDate), timeZone) : null,
                ].filter(Boolean);
                const rowState = marking[String(prize.tournamentId)];
                let markLabel = "تسجيل التسليم";
                if (rowState === "confirm") markLabel = "اضغط للتأكيد";
                if (rowState === "saving") markLabel = "جارٍ الحفظ…";
                return (
                  <li key={String(prize.tournamentId)} className={styles.row}>
                    <span className={styles.rowText}>
                      <span className={styles.name} dir="auto">
                        {prize.name || "بطولة"}
                      </span>
                      {meta.length > 0 && <span className={styles.meta}>{meta.join(" · ")}</span>}
                    </span>
                    {canMarkDelivered && prize.tableId && (
                      <button
                        type="button"
                        className={`${styles.markButton} ${rowState === "confirm" ? styles.markConfirm : ""}`}
                        onClick={() => markDelivered(prize)}
                        disabled={rowState === "saving"}
                        aria-label={`${markLabel}: ${prize.name || "بطولة"}`}
                      >
                        {rowState === "saving" ? (
                          <Loader size={12} />
                        ) : (
                          <CheckCircleIcon className={styles.markIcon} />
                        )}
                        <span>{markLabel}</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            {canOpenPrizePage ? (
              <button
                type="button"
                className={styles.footerLink}
                onClick={() => {
                  close(false);
                  navigate(PRIZE_PAGE);
                }}
              >
                فتح صفحة تسليم الجوائز
              </button>
            ) : (
              <div className={styles.footer}>تُسجَّل كمسلّمة من صفحة تسليم الجوائز.</div>
            )}
          </div>
        </Portal>
      )}
    </>
  );
};

export default PendingPrizesBadge;
